import NiceModal from '@ebay/nice-modal-react';
import { ActionIcon, Badge, Group, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconArchive, IconEdit, IconTrash } from '@tabler/icons-react';

import { useEditContentMaterial, useRemoveContentMaterial } from '../../api';
import { BulkUploadType } from '../../constants/mediaConfig';
import {
  getStatusBadgeColor,
  isStatusTransitionAllowed,
  STATUS_LABELS,
} from '../../constants/status';
import { contentTypes } from '../../types';
import { formatCreatedDate, formatFileSize } from '../../utils/format';
import { EditMaterialDrawer } from '../EditMaterialDrawer';
import { EditTextDrawer } from '../EditTextDrawer';
import { ActivateIcon, DeactivateIcon } from '../icons/StatusActionIcons';
import { isPreviewableMedia } from '../MediaPreviewModal';
import { MediaThumbnail } from '../MediaThumbnail';

import { TypeIcon } from './TypeIcon';

import { ConfirmModal } from '@/components/Modals/Confirm';

type Props = {
  item: contentTypes.Item;
  pageId?: string;
  managerId?: string;
};

const isMediaType = (
  type: contentTypes.ContentType
): type is Exclude<contentTypes.ContentType, 'text'> => type !== 'text';

const canTransitionTo = (from: contentTypes.Status, to: contentTypes.Status) =>
  from !== to && isStatusTransitionAllowed(from, to);

export const BodyRow = ({ item, pageId, managerId }: Props) => {
  const editMaterial = useEditContentMaterial({ pageId });
  const removeMaterial = useRemoveContentMaterial({ pageId });

  const patchStatus = (status: contentTypes.Status) => {
    editMaterial.mutate({ id: item._id, status, managerId });
  };

  const handleEdit = () => {
    if (item.type === 'text') {
      NiceModal.show(EditTextDrawer, {
        itemId: item._id,
        pageId,
        managerId,
        initialValues: {
          name: item.name,
          category: item.category,
          status: item.status,
          textContent: item.textContent ?? '',
        },
      });
      return;
    }

    if (!isMediaType(item.type)) return;

    NiceModal.show(EditMaterialDrawer, {
      itemId: item._id,
      pageId,
      platform: item.platform,
      managerId,
      initialValues: {
        type: item.type,
        name: item.name,
        category: item.category,
        status: item.status,
        filename: item.filename ?? '',
        size: item.size,
      },
    });
  };

  const handleDelete = () =>
    NiceModal.show(ConfirmModal, {
      title: `Удалить «${item.name}»?`,
      onSuccess: () => removeMaterial.mutate({ id: item._id, managerId }),
    });

  const handleArchive = () => patchStatus('archived');
  const handleActivate = () => patchStatus('active');
  const handleDeactivate = () => patchStatus('inactive');

  const subtitle = item.type === 'text' ? item.textContent : item.filename;
  const canDeactivate = canTransitionTo(item.status, 'inactive');
  const canActivate = canTransitionTo(item.status, 'active');
  const canArchive = canTransitionTo(item.status, 'archived');
  const isPreviewable = isPreviewableMedia(item.type);
  const previewUrl = item.url ?? null;

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          {isPreviewable ? (
            <MediaThumbnail
              mediaType={item.type as BulkUploadType}
              previewUrl={previewUrl}
              title={item.name}
            />
          ) : (
            <TypeIcon type={item.type} />
          )}

          <Stack gap={2}>
            <Text size="sm" fw={600}>
              {item.name}
            </Text>
            {subtitle && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {subtitle}
              </Text>
            )}
          </Stack>
        </Group>
      </Table.Td>

      <Table.Td>
        <Text size="sm">{item.category}</Text>
      </Table.Td>

      <Table.Td>
        <Text size="sm">{formatCreatedDate(item.createdAt)}</Text>
      </Table.Td>

      <Table.Td>
        <Badge variant="light" color={getStatusBadgeColor(item.status)} radius="sm">
          {STATUS_LABELS[item.status]}
        </Badge>
      </Table.Td>

      <Table.Td>
        <Text size="sm">{formatFileSize(item.size)}</Text>
      </Table.Td>

      <Table.Td>
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          <Tooltip label="Редактировать" withArrow>
            <ActionIcon variant="subtle" radius="md" onClick={handleEdit}>
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>

          {canDeactivate && (
            <Tooltip label="Деактивировать" withArrow>
              <ActionIcon variant="subtle" radius="md" onClick={handleDeactivate}>
                <DeactivateIcon />
              </ActionIcon>
            </Tooltip>
          )}

          {canActivate && (
            <Tooltip label={item.status === 'archived' ? 'Восстановить' : 'Активировать'} withArrow>
              <ActionIcon variant="subtle" radius="md" onClick={handleActivate}>
                <ActivateIcon />
              </ActionIcon>
            </Tooltip>
          )}

          {canArchive && (
            <Tooltip label="Архивировать" withArrow>
              <ActionIcon variant="subtle" radius="md" onClick={handleArchive}>
                <IconArchive size={16} />
              </ActionIcon>
            </Tooltip>
          )}

          <Tooltip label="Удалить" withArrow>
            <ActionIcon variant="subtle" radius="md" color="red" onClick={handleDelete}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
